import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCirclePlus, IconExternalLink, IconTrash } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

const CreateSchema = z.object({
  title: z.string().trim().min(2).max(200),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'kebab-case')
    .max(120),
});
type CreateForm = z.infer<typeof CreateSchema>;

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function CmsPagesIndex() {
  const list = trpc.admin.pages.list.useQuery();
  const utils = trpc.useUtils();
  const [opened, { open, close }] = useDisclosure(false);
  const [onlyMenu, setOnlyMenu] = useState(false);
  const slugTouched = useRef(false);

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: { title: '', slug: '' },
  });

  const create = trpc.admin.pages.create.useMutation({
    onSuccess: () => {
      void utils.admin.pages.list.invalidate();
      close();
      notifications.show({ message: 'Page created', color: 'green' });
      form.reset();
      slugTouched.current = false;
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const del = trpc.admin.pages.delete.useMutation({
    onSuccess: () => void utils.admin.pages.list.invalidate(),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // Slug auto-derivation: while the slug field hasn't been hand-edited,
  // mirror slugify(title) into it on every keystroke.
  const titleValue = form.watch('title');
  useEffect(() => {
    if (slugTouched.current) return;
    form.setValue('slug', slugify(titleValue ?? ''), { shouldValidate: true });
  }, [titleValue, form]);

  // Reset the touched flag whenever the modal reopens.
  useEffect(() => {
    if (opened) slugTouched.current = false;
  }, [opened]);

  const filtered = (list.data ?? []).filter((p) => (onlyMenu ? p.showInMenu === 1 : true));

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>CMS pages</Title>
          <Group>
            <Switch
              label="Show only menu pages"
              checked={onlyMenu}
              onChange={(e) => setOnlyMenu(e.currentTarget.checked)}
            />
            <Button leftSection={<IconCirclePlus size={16} />} onClick={open}>
              New page
            </Button>
          </Group>
        </Group>
        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={200} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Slug</Table.Th>
                  <Table.Th>Menu</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td>
                      <Anchor
                        renderRoot={(props) => (
                          <Link to="/central/cms-pages/$uid" params={{ uid: p.uid }} {...props} />
                        )}
                      >
                        {p.title}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Anchor href={`/page/${p.slug}`} target="_blank" size="sm">
                        /page/{p.slug} <IconExternalLink size={12} style={{ display: 'inline' }} />
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      {p.showInMenu === 1 && (
                        <Badge size="xs" variant="light">
                          shown
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td align="right">
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => del.mutate({ pageId: p.id })}
                        aria-label="Delete"
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {filtered.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text c="dimmed" ta="center" py="md">
                        {(list.data ?? []).length === 0
                          ? 'No pages yet.'
                          : 'No pages match the current filter.'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
        <Modal opened={opened} onClose={close} title="New page" centered>
          <form onSubmit={form.handleSubmit((v) => create.mutate(v))}>
            <Stack gap="sm">
              <TextInput
                label="Title"
                {...form.register('title')}
                error={form.formState.errors.title?.message}
              />
              <TextInput
                label="Slug"
                description="Auto-derived from the title until you edit it."
                {...form.register('slug', {
                  onChange: () => {
                    slugTouched.current = true;
                  },
                })}
                error={form.formState.errors.slug?.message}
              />
              <Button type="submit" loading={create.isPending}>
                Create
              </Button>
            </Stack>
          </form>
        </Modal>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/central/cms-pages/')({ component: CmsPagesIndex });
